import React, { useEffect, useRef, useState } from "react";
import type { GroupChannel, SendbirdGroupChat, GroupChannelCollection } from "@sendbird/chat/groupChannel";
import { GroupChannelHandler, GroupChannelFilter, MemberState } from "@sendbird/chat/groupChannel";
import { getSendbird } from "../sendbird";
import { SendbirdUser } from "../chat";
import {
    ChannelAvatar,
    ChannelAvatarImage,
    ChannelAvatarWrapper,
    ChannelDate,
    ChannelDot,
    ChannelGroup,
    ChannelGroupHeader,
    ChannelGroupTitle,
    ChannelInfo,
    ChannelLastMessage,
    ChannelMeta,
    ChannelName,
    ChannelNameRow,
    ChannelRow,
    ChannelStatus,
    ChannelRowContent,
    AssignToMeButton,
    CoachPageWrapper,
    FilterWrapper,
    FlagBadge,
    FlagBadgesWrapper,
    MessagesFoundLabel,
    SkeletonSearchBar,
    SkeletonFilterItem,
    SkeletonFilterCircle,
    SkeletonFilterLabel,
    SkeletonChannelRow,
    SkeletonAvatar,
    SkeletonChannelInfo,
    SkeletonBar,
    SkeletonMetaBlock,
    EmptyChannelsPlaceholder,
    EmptyChannelsText,
    OverdueAlert,
} from "./index.styled";
import ChannelsFilterIndicator from "./components/channelsFilterIndicator";
import { axiosRequestData } from "../../../helpers/axios";
import { SVGAllMessagesIcon, SVGLockIcon, SVGOverdueIcon, SVGReadIcon, SVGRespondSoonIcon } from "./icons";
import SearchBar from "./components/searchBar";
import { pgVIPCoachSetFilter, pgVIPCoachSearch, pgVIPCoachAssignToMe } from "../../mixPanelEvents/mixPanelEvents";

export type ChannelState = 'all' | 'overdue' | 'respond_soon' | 'read' | 'unread' | 'unassigned';

const filterSourceLabels: Record<ChannelState, string> = {
    all:          'All',
    overdue:      'Overdue',
    respond_soon: 'Respond Soon',
    read:         'Read',
    unread:       'Unread',
    unassigned:   'Unassigned',
};

export interface CoachPageProps {
    user?: SendbirdUser | null;
    onOpenChat: () => void;
    setSelectedChannelUrl?: (url: string, isInactive?: boolean) => void;
}

const AUTORESPONDER_ID = "autoresponder";
const ADMIN = "admin";
const ONE_WEEK_MS = 7 * 24 * 60 * 60 * 1000;
const ONE_MONTH_MS = 30 * 24 * 60 * 60 * 1000;
const SIX_MONTHS_MS = 180 * 24 * 60 * 60 * 1000;

const AVATAR_COLORS = [
    '#2e2a28', '#3b5bdb', '#2f9e44', '#e67700',
    '#c92a2a', '#5c7cfa', '#0c8599', '#862e9c',
];

const getAvatarColor = (userId: string) => {
    let hash = 0;
    for (let i = 0; i < userId.length; i++) hash = userId.charCodeAt(i) + ((hash << 5) - hash);
    return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const getInitials = (name: string) =>
    name.split(' ').filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('');

const formatChannelDate = (ts: number) => {
    const d = new Date(ts);
    return {
        date: d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }),
        time: d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
    };
};

const getLastMessagePreview = (ch: GroupChannel): string => {
    const msg = ch.lastMessage;
    if (!msg) return '';
    if (msg.messageType === 'file') return (msg as any).name ?? 'File';
    return (msg as any).message ?? '';
};

const CoachPage: React.FC<CoachPageProps> = ({ user, onOpenChat, setSelectedChannelUrl }) => {
    const [channels, setChannels] = useState<GroupChannel[]>([]);
    const [allChannels, setAllChannels] = useState<GroupChannel[]>([]);
    const [overdueChannels, setOverdueChannels] = useState<GroupChannel[]>([]);
    const [respondSoonChannels, setRespondSoonChannels] = useState<GroupChannel[]>([]);
    const [readChannels, setReadChannels] = useState<GroupChannel[]>([]);
    const [unreadChannels, setUnreadChannels] = useState<GroupChannel[]>([]);
    const [unassignedChannels, setUnassignedChannels] = useState<GroupChannel[]>([]);
    const [userFlags, setUserFlags] = useState<Record<string, string[]>>({});
    const [currentUserId, setCurrentUserId] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [stateFilter, setStateFilter] = useState<ChannelState>('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [isAssigning, setIsAssigning] = useState(false);
    const [assignedChannelUrls, setAssignedChannelUrls] = useState<Set<string>>(new Set());
    const [hasMore, setHasMore] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [alertVisible, setAlertVisible] = useState(false);
    const alertTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const queryRef = useRef<any>(null);
    const sentinelRef = useRef<HTMLDivElement>(null);
    const allRawRef = useRef<GroupChannel[]>([]);
    const fetchedFlagIdsRef = useRef<Set<string>>(new Set());
    const sbRef = useRef<SendbirdGroupChat | null>(null);
    const collectionRef = useRef<GroupChannelCollection | null>(null);
    const computeRef = useRef<((raw: GroupChannel[]) => void) | null>(null);
    const searchQueryRef = useRef<any>(null);
    const searchRawRef = useRef<GroupChannel[]>([]);
    const searchTermRef = useRef<string>('');

    useEffect(() => {
        pgVIPCoachSetFilter({ Source: 'All' });
    }, []);

    const handleFilterSelect = (filter: ChannelState) => {
        setStateFilter(filter);
        pgVIPCoachSetFilter({ Source: filterSourceLabels[filter] });
    };

    const handleSearchChange = (term: string) => {
        if (searchTerm === '' && term !== '') {
            pgVIPCoachSearch();
        }
        const trimmed = term.trim().toLowerCase();
        if (trimmed !== searchTermRef.current) {
            searchQueryRef.current = null;
            searchRawRef.current = [];
            if (!trimmed) {
                computeAndSetChannels(allRawRef.current);
                setHasMore(queryRef.current?.hasNext ?? false);
            }
        }
        searchTermRef.current = trimmed;
        setSearchTerm(term);
    };

    const overdueMs = (user?.flags?.overdue ?? 72) * 60 * 60 * 1000;
    const dueSoonMs = (user?.flags?.duesoon ?? 48) * 60 * 60 * 1000;

    const computeAndSetChannels = (raw: GroupChannel[]) => {
        const coachId = user?.user_id ?? '';
        const ts = Date.now();

        const withSubscriber = raw.filter(ch =>
            ch.members.some(
                m => m.state === MemberState.JOINED &&
                    m.userId !== coachId &&
                    m.userId !== AUTORESPONDER_ID &&
                    m.userId !== ADMIN
            )
        );

        const computedOverdue = withSubscriber.filter(ch => {
            const lastSenderId = (ch.lastMessage as any)?.sender?.userId;
            const elapsed = ch.lastMessage ? ts - ch.lastMessage.createdAt : Infinity;
            return !!lastSenderId && lastSenderId !== coachId && elapsed > overdueMs;
        });

        const computedRespondSoon = withSubscriber.filter(ch => {
            const lastSenderId = (ch.lastMessage as any)?.sender?.userId;
            const elapsed = ch.lastMessage ? ts - ch.lastMessage.createdAt : Infinity;
            return !!lastSenderId && lastSenderId !== coachId && elapsed > dueSoonMs && elapsed <= overdueMs;
        });

        const computedUnread = withSubscriber.filter(ch => {
            const lastSenderId = (ch.lastMessage as any)?.sender?.userId;
            const elapsed = ch.lastMessage ? ts - ch.lastMessage.createdAt : Infinity;
            return !!lastSenderId && lastSenderId !== coachId && elapsed <= dueSoonMs && ch.unreadMessageCount > 0;
        });

        const computedRead = withSubscriber.filter(ch => {
            const lastSenderId = (ch.lastMessage as any)?.sender?.userId;
            const elapsed = ch.lastMessage ? ts - ch.lastMessage.createdAt : Infinity;
            return !!lastSenderId && lastSenderId !== coachId && elapsed <= dueSoonMs && ch.unreadMessageCount === 0;
        });

        setChannels(raw);
        setAllChannels(withSubscriber);
        setOverdueChannels(computedOverdue);
        setRespondSoonChannels(computedRespondSoon);
        setReadChannels(computedRead);
        setUnreadChannels(computedUnread);

        if (computedOverdue.length > 0 || computedRespondSoon.length > 0) {
            setAlertVisible(true);
            if (alertTimerRef.current) clearTimeout(alertTimerRef.current);
            alertTimerRef.current = setTimeout(() => setAlertVisible(false), 5000);
        }
    };

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                if (!cancelled && user) setCurrentUserId(user.user_id);
                const sb = await getSendbird();
                sbRef.current = sb;
                await sb.connect(user!.user_id, user!.access_token);

                const groupChannelFilter = new GroupChannelFilter();
                groupChannelFilter.includeEmpty = true;

                const collection = sb.groupChannel.createGroupChannelCollection({
                    filter: groupChannelFilter,
                });
                collectionRef.current = collection;
                collection.setGroupChannelCollectionHandler({
                    onChannelsAdded: (_context, channels) => {
                        const newChannels = channels as unknown as GroupChannel[];
                        allRawRef.current = [...newChannels, ...allRawRef.current];
                        computeRef.current?.(allRawRef.current);
                        setStateFilter('all');
                    },
                });
                if (collection.hasMore) {
                    await collection.loadMore();
                }

                const query = sb.groupChannel.createMyGroupChannelListQuery({
                    limit: 20,
                    includeEmpty: true,
                });
                queryRef.current = query;
                const batch = await query.next();
                allRawRef.current = batch;

                if (!cancelled) {
                    computeAndSetChannels(batch);
                    setHasMore(query.hasNext);
                }

                // Collect all vip_user_ IDs from initial batch + unassigned channels
                const assignedVipIds = batch.flatMap((ch: GroupChannel) => ch.members
                    .map((m: any) => m.userId)
                    .filter((id: string) => id.startsWith('vip_user_'))
                );
                const unassignedVipIds = (user?.unassigned_channels ?? [])
                    .map((uc: any) => uc.user.user_id)
                    .filter((id: string) => id.startsWith('vip_user_'));
                const vipUserIds = Array.from(new Set([...assignedVipIds, ...unassignedVipIds]));
                if (vipUserIds.length) {
                    vipUserIds.forEach((id: string) => fetchedFlagIdsRef.current.add(id));
                    const flags = await getUserFlags(vipUserIds);
                    if (!cancelled) setUserFlags(flags);
                }

                if (user?.unassigned_channels?.length) {
                    const mapped = await mappingUnassignedChannels(user.unassigned_channels);
                    if (!cancelled) setUnassignedChannels(mapped);
                }
            } catch (err) {
                console.error("Failed to load coach channels:", err);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => {
            cancelled = true;
            collectionRef.current?.dispose();
            collectionRef.current = null;
        };
    }, []);

    const getUserFlags = async (userIds: string[]): Promise<Record<string, string[]>> => {
        try {
            const res = await axiosRequestData('POST', 'wp-json/pg/v1/vip/userflags', { users: userIds });
            return (res.data?.users ?? {}) as Record<string, string[]>;
        } catch (err) {
            console.error('Failed to get user flags:', err);
            return {};
        }
    };

    const mappingUnassignedChannels = async (unassigned_channels: any[]): Promise<GroupChannel[]> => {
        const channelUrls = unassigned_channels.map(uc => uc.channel_url);

        return unassigned_channels.map(uc => ({
            url: uc.channel_url,
            name: '',
            cachedMetaData: uc.metadata ?? {},
            members: [{
                userId: uc.user.user_id,
                nickname: uc.user.nickname,
                profileUrl: uc.user.profile_url,
                state: MemberState.JOINED,
            }],
            lastMessage: {
                createdAt: uc.created_at * 1000,
                sender: {
                    userId: uc.user.user_id,
                    nickname: uc.user.nickname,
                    profileUrl: uc.user.profile_url,
                },
                messageType: 'user',
                message: '',
            } as any,
        } as GroupChannel));
    }

    const loadingMoreRef = useRef(false);

    const loadMoreChannels = async () => {
        if (stateFilter !== 'all') return;
        if (loadingMoreRef.current) return;

        const activeTerm = searchTermRef.current;

        if (activeTerm) {
            if (!sbRef.current) return;
            if (!searchQueryRef.current) {
                searchQueryRef.current = sbRef.current.groupChannel.createMyGroupChannelListQuery({
                    limit: 20,
                    includeEmpty: true,
                    nicknameContainsFilter: activeTerm,
                });
            }
            if (!searchQueryRef.current.hasNext) return;
            loadingMoreRef.current = true;
            setLoadingMore(true);
            try {
                const batch = await searchQueryRef.current.next();
                searchRawRef.current = [...searchRawRef.current, ...batch];
                computeAndSetChannels(searchRawRef.current);
                setHasMore(searchQueryRef.current.hasNext);

                const newVipIds = batch
                    .flatMap((ch: GroupChannel) => ch.members.map((m: any) => m.userId))
                    .filter((id: string) => id.startsWith('vip_user_') && !fetchedFlagIdsRef.current.has(id));
                const uniqueNewIds: string[] = Array.from(new Set(newVipIds));
                if (uniqueNewIds.length) {
                    uniqueNewIds.forEach(id => fetchedFlagIdsRef.current.add(id));
                    const newFlags = await getUserFlags(uniqueNewIds);
                    setUserFlags(prev => ({ ...prev, ...newFlags }));
                }
            } catch (err) {
                console.error('Failed to load more search channels:', err);
            } finally {
                loadingMoreRef.current = false;
                setLoadingMore(false);
            }
            return;
        }

        if (!queryRef.current?.hasNext) return;
        loadingMoreRef.current = true;
        setLoadingMore(true);
        try {
            const batch = await queryRef.current.next();
            allRawRef.current = [...allRawRef.current, ...batch];
            computeAndSetChannels(allRawRef.current);
            setHasMore(queryRef.current.hasNext);

            const newVipIds = batch
                .flatMap((ch: GroupChannel) => ch.members.map((m: any) => m.userId))
                .filter((id: string) => id.startsWith('vip_user_') && !fetchedFlagIdsRef.current.has(id));
            const uniqueNewIds: string[] = Array.from(new Set(newVipIds));
            if (uniqueNewIds.length) {
                uniqueNewIds.forEach(id => fetchedFlagIdsRef.current.add(id));
                const newFlags = await getUserFlags(uniqueNewIds);
                setUserFlags(prev => ({ ...prev, ...newFlags }));
            }
        } catch (err) {
            console.error('Failed to load more channels:', err);
        } finally {
            loadingMoreRef.current = false;
            setLoadingMore(false);
        }
    };

    const loadMoreRef = useRef(loadMoreChannels);
    useEffect(() => { loadMoreRef.current = loadMoreChannels; });
    useEffect(() => { computeRef.current = computeAndSetChannels; });

    useEffect(() => {
        if (loading || !sbRef.current) return;
        const handlerKey = 'coach-page-message-handler';
        const handler = new GroupChannelHandler({
            onMessageReceived: (channel) => {
                allRawRef.current = allRawRef.current.map(ch =>
                    ch.url === channel.url ? channel as GroupChannel : ch
                );
                computeRef.current?.(allRawRef.current);
            },
            onChannelChanged: (channel) => {
                allRawRef.current = allRawRef.current.map(ch =>
                    ch.url === channel.url ? channel as GroupChannel : ch
                );
                computeRef.current?.(allRawRef.current);
            },
        });
        sbRef.current.groupChannel.addGroupChannelHandler(handlerKey, handler);
        return () => {
            sbRef.current?.groupChannel.removeGroupChannelHandler(handlerKey);
        };
    }, [loading]);

    useEffect(() => {
        if (loading) return;
        const sentinel = sentinelRef.current;
        if (!sentinel) return;
        const observer = new IntersectionObserver(
            entries => { if (entries[0].isIntersecting) loadMoreRef.current(); },
            { threshold: 0 }
        );
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [loading]);

    useEffect(() => {
        const handleScroll = () => {
            const scrollBottom = window.scrollY + window.innerHeight;
            const docHeight = document.documentElement.scrollHeight;
            if (docHeight - scrollBottom < 300) loadMoreRef.current();
        };
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const now = Date.now();

    type ChannelFlag = 'overdue' | 'respond_soon' | 'unread' | 'read' | 'sent';

    const getChannelFlag = (ch: GroupChannel): ChannelFlag => {
        const lastSenderId = (ch.lastMessage as any)?.sender?.userId;
        const elapsed = ch.lastMessage ? now - ch.lastMessage.createdAt : Infinity;
        const subscriberSentLast = !!lastSenderId && lastSenderId !== currentUserId;

        if (!subscriberSentLast) return 'sent';
        if (elapsed > overdueMs) return 'overdue';
        if (elapsed > dueSoonMs) return 'respond_soon';
        if (ch.unreadMessageCount > 0) return 'unread';
        return 'read';
    };

    const canCoachAssign = (): boolean => true;

    const assignChannelToCoach = async (channelUrl: string): Promise<boolean> => {
        if (isAssigning) return false;
        try {
            setIsAssigning(true);
            const res = await axiosRequestData('POST', 'wp-json/pg/v1/vip/claim_channel', { channel_url: channelUrl });
            setUnassignedChannels(prev => prev.filter(ch => ch.url !== channelUrl));
            const claimed = res.data?.claim !== false && !!res.data?.success;
            if (!claimed) setStateFilter('all');
            return claimed;
        } catch (err) {
            console.error('Failed to assign channel:', err);
            setStateFilter('all');
            return false;
        } finally {
            pgVIPCoachAssignToMe({});
            setAssignedChannelUrls(prev => new Set(prev).add(channelUrl));
            pgVIPCoachSetFilter({ Source: 'All' });
            setIsAssigning(false);
        }
    };

    const getFilteredChannels = (filter: ChannelState): GroupChannel[] => {
        switch (filter) {
            case 'overdue':      return overdueChannels;
            case 'respond_soon': return respondSoonChannels;
            case 'read':         return readChannels;
            case 'unread':       return unreadChannels;
            case 'unassigned':   return unassignedChannels;
            default:             return allChannels;
        }
    };

    const filteredChannels = getFilteredChannels(stateFilter);

    const applySearch = (list: GroupChannel[]): GroupChannel[] => {
        const term = searchTerm.trim().toLowerCase();
        if (!term) return list;
        return list.filter(ch => {
            const subscriber = ch.members.find(
                m => m.userId !== currentUserId && m.userId !== AUTORESPONDER_ID && m.userId !== ADMIN
                && m.userId.startsWith('vip_support_') === false && m.userId.startsWith('vip_coach') === false
            );
            const name = (subscriber?.nickname || subscriber?.userId || '').toLowerCase();
            const parts = name.split(' ').filter(Boolean);
            return (
                name.includes(term) ||
                parts.some(p => p.startsWith(term))
            );
        });
    };

    const searchedChannels = applySearch(filteredChannels);

    const filterCounts: Record<ChannelState, number> = {
        all:          applySearch(allChannels).length,
        overdue:      applySearch(overdueChannels).length,
        respond_soon: applySearch(respondSoonChannels).length,
        read:         applySearch(readChannels).length,
        unread:       applySearch(unreadChannels).length,
        unassigned:   applySearch(unassignedChannels).length,
    };

    const readChannelUrlSet = new Set(readChannels.map(ch => ch.url));

    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    const START_OF_TODAY_MS = startOfToday.getTime();

    const today = searchedChannels.filter(ch =>
        ch.lastMessage && ch.lastMessage.createdAt >= START_OF_TODAY_MS
    );
    const lastWeek = searchedChannels.filter(ch =>
        ch.lastMessage &&
        ch.lastMessage.createdAt < START_OF_TODAY_MS &&
        (now - ch.lastMessage.createdAt) <= ONE_WEEK_MS
    );
    const lastMonth = searchedChannels.filter(ch =>
        ch.lastMessage &&
        (now - ch.lastMessage.createdAt) > ONE_WEEK_MS &&
        (now - ch.lastMessage.createdAt) <= ONE_MONTH_MS
    );
    const last6Months = searchedChannels.filter(ch =>
        ch.lastMessage &&
        (now - ch.lastMessage.createdAt) > ONE_MONTH_MS &&
        (now - ch.lastMessage.createdAt) <= SIX_MONTHS_MS
    );
    const last12Months = searchedChannels.filter(ch =>
        !ch.lastMessage || (now - ch.lastMessage.createdAt) > SIX_MONTHS_MS
    );

    const renderGroup = (title: string, items: GroupChannel[], isFirst: boolean = false) => {
        if (items.length === 0) return null;
        return (
            <ChannelGroup key={title} $isFirst={isFirst}>
                <ChannelGroupHeader $isFirst={isFirst}>
                    <ChannelGroupTitle>{title}</ChannelGroupTitle>
                    <MessagesFoundLabel> {items.length} Messages Found</MessagesFoundLabel>
                </ChannelGroupHeader>
                {items.map(ch => {
                    const subscriber = ch.members.find(
                        m => m.userId !== currentUserId && m.userId !== AUTORESPONDER_ID && m.userId !== ADMIN
                        && !m.userId.startsWith('vip_coach') && !m.userId.startsWith('vip_support_')
                    );
                    const displayName = subscriber?.nickname || subscriber?.userId || ch.name || 'Unknown';
                    const initials = getInitials(displayName);
                    const avatarColor = getAvatarColor(subscriber?.userId ?? ch.url);
                    const channelFlag = getChannelFlag(ch);
                    const isOverdue = channelFlag === 'overdue';
                    const isRespondSoon = channelFlag === 'respond_soon';
                    const isSent = channelFlag === 'sent';
                    const isRead = ch.unreadMessageCount === 0;
                    const subscriberFlags = subscriber?.userId ? (userFlags[subscriber.userId] ?? []) : [];
                    const isInactive = !!(subscriber?.userId?.startsWith('vip_user_') && !subscriberFlags.includes('vip'));
                    const canAssignThis = stateFilter === 'unassigned'
                        ? canCoachAssign() && !isAssigning
                        : false;
                    const displayFlags = subscriberFlags.filter(f => f !== 'vip');
                    const { date, time } = ch.lastMessage
                        ? formatChannelDate(ch.lastMessage.createdAt)
                        : { date: '', time: '' };
                    
                    return (
                        <ChannelRow
                            key={ch.url}
                            $isInactive={stateFilter !== 'unassigned' && isInactive}
                            $isUnassigned={stateFilter === 'unassigned'}
                            $isRead={stateFilter !== 'unassigned' && isRead}
                            $isAssigned={assignedChannelUrls.has(ch.url)}
                            onClick={stateFilter !== 'unassigned' ? () => {
                                setSelectedChannelUrl?.(ch.url, isInactive);
                                onOpenChat();
                            } : undefined}
                        >
                            <ChannelRowContent $isInactive={stateFilter !== 'unassigned' && isInactive} $alignStart={stateFilter === 'unassigned' && displayFlags.length > 0} $state={stateFilter}>
                            <ChannelAvatarWrapper>
                                <ChannelAvatar $color={avatarColor}>
                                    {subscriber?.profileUrl
                                        ? <ChannelAvatarImage src={subscriber.profileUrl} alt={displayName} />
                                        : initials
                                    }
                                </ChannelAvatar>
                                {stateFilter === 'unassigned'
                                    ? <ChannelDot $variant="teal" />
                                    : <ChannelDot $variant={
                                        isOverdue                      ? 'red'    :
                                        isRespondSoon                  ? 'yellow' :
                                        readChannelUrlSet.has(ch.url)  ? 'black'  :
                                        'blue'
                                    } />
                                }
                            </ChannelAvatarWrapper>
                            <ChannelInfo>
                                <ChannelNameRow $hasBadges={displayFlags.length > 0}>
                                    <ChannelName>{displayName}</ChannelName>
                                    {(displayFlags.length > 0) && (
                                        <FlagBadgesWrapper>
                                            {displayFlags.map(flag => (
                                                <FlagBadge key={flag} $flag={flag}>
                                                    {flag === 'vip_experience' ? 'Golf School Attendee' :
                                                     flag === 'vip_elite' ? 'Elite' :
                                                     flag.replace(/_/g, ' ')}
                                                </FlagBadge>
                                            ))}
                                        </FlagBadgesWrapper>
                                    )}
                                </ChannelNameRow>
                                <ChannelLastMessage>{getLastMessagePreview(ch)}</ChannelLastMessage>
                            </ChannelInfo>
                            <ChannelMeta>
                                {date && (
                                    <ChannelDate $isOverdue={stateFilter !== 'unassigned' && isOverdue}>
                                        {date}<br />{time}
                                    </ChannelDate>
                                )}
                                {stateFilter === 'unassigned' ? (
                                    <AssignToMeButton
                                        $disabled={!canAssignThis}
                                        $hasBadges={displayFlags.length > 0}
                                        $longName={displayName.length >= 15}
                                        disabled={!canAssignThis}
                                        onClick={async (e: React.MouseEvent<HTMLElement>) => {
                                            e.stopPropagation();
                                            if (!canAssignThis) return;
                                            const success = await assignChannelToCoach(ch.url);
                                            if (success) {
                                                setSelectedChannelUrl?.(ch.url);
                                                onOpenChat();
                                            }
                                        }}
                                    >
                                        Assign to Me
                                    </AssignToMeButton>
                                ) : (
                                    <>
                                        {isOverdue && <ChannelStatus $variant="overdue">Overdue</ChannelStatus>}
                                        {isRespondSoon && <ChannelStatus $variant="respond_soon">Respond Soon</ChannelStatus>}
                                        {isSent && <ChannelStatus $variant="sent">Sent</ChannelStatus>}
                                    </>
                                )}
                            </ChannelMeta>
                            </ChannelRowContent>
                        </ChannelRow>
                    );
                })}
            </ChannelGroup>
        );
    };

    if (loading) return (
        <CoachPageWrapper>
            <SkeletonSearchBar variant="rectangular" />
            <FilterWrapper>
                {Array.from({ length: 6 }).map((_, i) => (
                    <SkeletonFilterItem key={i}>
                        <SkeletonFilterCircle variant="circular" />
                        <SkeletonFilterLabel variant="rectangular" />
                    </SkeletonFilterItem>
                ))}
            </FilterWrapper>
            {Array.from({ length: 8 }).map((_, i) => (
                <SkeletonChannelRow key={i}>
                    <SkeletonAvatar variant="circular" />
                    <SkeletonChannelInfo>
                        <SkeletonBar variant="rectangular" $w="55%" $h="18px" />
                        <SkeletonBar variant="rectangular" $w="75%" $h="14px" />
                    </SkeletonChannelInfo>
                    <SkeletonMetaBlock variant="rectangular" />
                </SkeletonChannelRow>
            ))}
        </CoachPageWrapper>
    );

    return (
        <CoachPageWrapper>
            <SearchBar searchTerm={searchTerm} setSearchTerm={handleSearchChange} onClear={() => handleSearchChange('')} />
            <FilterWrapper>
                <ChannelsFilterIndicator filter="all" count={filterCounts.all} stateFilter={stateFilter} onSelect={handleFilterSelect} icon={SVGAllMessagesIcon} />
                <ChannelsFilterIndicator filter="overdue" count={filterCounts.overdue} stateFilter={stateFilter} onSelect={handleFilterSelect} icon={SVGOverdueIcon} />
                <ChannelsFilterIndicator filter="respond_soon" count={filterCounts.respond_soon} stateFilter={stateFilter} onSelect={handleFilterSelect} icon={SVGRespondSoonIcon} />
                <ChannelsFilterIndicator filter="read" count={filterCounts.read} stateFilter={stateFilter} onSelect={handleFilterSelect} icon={SVGReadIcon} />
                <ChannelsFilterIndicator filter="unread" count={filterCounts.unread} stateFilter={stateFilter} onSelect={handleFilterSelect} icon={SVGReadIcon} />
                <ChannelsFilterIndicator filter="unassigned" count={filterCounts.unassigned} stateFilter={stateFilter} onSelect={handleFilterSelect} icon={SVGReadIcon} />
            </FilterWrapper>
            {stateFilter !== 'unassigned' && (
                <OverdueAlert $visible={alertVisible} role="alert">
                    {SVGLockIcon}
                    <span>You have overdue/reply soon messages</span>
                </OverdueAlert>
            )}
            {stateFilter === 'unassigned' && !canCoachAssign() && (
                <OverdueAlert $visible={true} role="alert">
                    {SVGLockIcon}
                    <span>Review Swing in your Queue to Unlock.</span>
                </OverdueAlert>
            )}
            {stateFilter === 'unassigned' ?
                renderGroup("Unassigned", searchedChannels, true) : 
                <>
                    {renderGroup("Today", today, true)}
                    {renderGroup("Last Week", lastWeek)}
                    {renderGroup("Last Month", lastMonth)}
                    {renderGroup("Last 6 Months", last6Months)}
                    {renderGroup("Last 12 Months", last12Months)}
                    {today.length === 0 && lastWeek.length === 0 && lastMonth.length === 0 && last6Months.length === 0 && last12Months.length === 0 && (
                        <EmptyChannelsPlaceholder>
                            <EmptyChannelsText>'0' messages found</EmptyChannelsText>
                        </EmptyChannelsPlaceholder>
                    )}
                </>
            }
            {loadingMore && Array.from({ length: 3 }).map((_, i) => (
                <SkeletonChannelRow key={`load-more-${i}`}>
                    <SkeletonAvatar variant="circular" />
                    <SkeletonChannelInfo>
                        <SkeletonBar variant="rectangular" $w="55%" $h="18px" />
                        <SkeletonBar variant="rectangular" $w="75%" $h="14px" />
                    </SkeletonChannelInfo>
                    <SkeletonMetaBlock variant="rectangular" />
                </SkeletonChannelRow>
            ))}
            <div ref={sentinelRef} />
        </CoachPageWrapper>
    );
};

export default CoachPage;
